// src/components/FlyerManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { firestore } from '../firebase';
import { isDemandOnlyUsageLabel } from '../utils/ticketUsage';
import { getDisplayName, getFirstName, getFreshlingDisplayName, getAgeFromYear } from '../utils/flyerDisplay';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
  arrayUnion,
  query,
  where,
  writeBatch,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import FlyerDetailModal from './FlyerDetailModal';
import HomeDzTab from './HomeDzTab';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  FM_PAIRING_EXCLUDED,
  isRegistrationMentorApproved,
} from '../utils/mentorEligibility';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import './FlyerManagement.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const FLYER_TABLE_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'type', label: 'Type', default: true },
  { id: 'email', label: 'Email', default: false },
  { id: 'test', label: 'Test', default: false },
  { id: 'manifestCheckIn', label: 'Manifest check-in', default: true },
  { id: 'subEvents', label: 'Sub-events', default: true },
  { id: 'skydives', label: 'Skydives', default: true },
  { id: 'sheetOrder', label: 'Sheet #', default: false },
  { id: 'rec', label: 'Rec', default: true },
  { id: 'recUpdate', label: 'Rec update', default: true },
  { id: 'status', label: 'Status', default: true },
  { id: 'emailsSent', label: 'Emails sent', default: true },
  { id: 'team', label: 'Team', default: true },
];

const FLYER_TYPES = ['mentor', 'freshling', 'volunteer'];

/** When merging two flyer docs for the same email, prefer mentor > freshling > volunteer. */
function flyerTypeRank(t) {
  if (t === 'mentor') return 3;
  if (t === 'freshling') return 2;
  if (t === 'volunteer') return 1;
  return 0;
}

// Sub-event labels from manifest tickets that we want to surface.
const SUB_EVENT_DEFS = [
  { key: 'Fury Lab', shortLabel: 'Fury Lab' },
  { key: 'Water Training (Thur)', shortLabel: 'Water Thu' },
  { key: 'Canopy Thursday', shortLabel: 'Canopy Thu' },
];

// Human-friendly labels for email templates.
const EMAIL_TEMPLATE_LABELS = {
  recurrency_update: 'Recurrency',
  recurrency_class_reminder: 'Recurrency class reminder',
  mentor_warning: 'Mentor warn',
  team_intro: 'Team intro',
  early_load_warning: 'Early load',
  mentor_load_summary: 'Load summary',
  after_party_desc_budget: 'After party',
  custom: 'Custom',
};

const FURY_REGISTRATION_HELP_TEXT =
  'Registrations sync from Fury Registration (Admin Dashboard → Import all). A Google Sheet link is optional for this event.';

const FlyerManagement = () => {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('eventId') || '';
  const flyerParam = (searchParams.get('flyer') || '').toLowerCase();
  const searchFilterParam = (searchParams.get('q') || '').toLowerCase();
  // State for active events
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [furyRegistrationHelpOpen, setFuryRegistrationHelpOpen] = useState(false);

  // State for flyers (filtered by event)
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state for add/edit flyer
  const [modalOpen, setModalOpen] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [flyerFormInvalidNames, setFlyerFormInvalidNames] = useState(() => new Set());

  // Details modal for per-flyer full info
  const [detailsFlyer, setDetailsFlyer] = useState(null);
  // Editable notes in details modal (synced when detailsFlyer opens/changes)
  const [detailsNotesEdit, setDetailsNotesEdit] = useState('');
  const [savingDetailsNotes, setSavingDetailsNotes] = useState(false);
  const [savingManifestCheckInRequired, setSavingManifestCheckInRequired] = useState(false);
  const [savingManifestTickets, setSavingManifestTickets] = useState(false);

  // Teams for this event (to show whether flyers are paired)
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!modalOpen) setFlyerFormInvalidNames(new Set());
  }, [modalOpen]);

  useModalEscapeKey(advancedSettingsOpen, () => setAdvancedSettingsOpen(false));
  useModalEscapeKey(modalOpen, () => setModalOpen(false));

  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();
  const [flyerTableExport, setFlyerTableExport] = useState(null); // { mode } | null
  const [flyerExportCols, setFlyerExportCols] = useState(() => new Set());

  // Flyer form state (for add/edit)
  const [newFlyer, setNewFlyer] = useState({
    email: '',
    firstName: '',
    lastName: '',
    type: 'mentor',
    jumpNumber: '',
    notes: '',
    test: false,
  });
  // Holds the document ID of the flyer being edited (if any)
  const [editingFlyerId, setEditingFlyerId] = useState(null);

  // Selected event details (for Google Sheet link)
  const [eventDetails, setEventDetails] = useState(null);

  // Tab navigation
  const [activeTab, setActiveTab] = useState('flyers');

  // Table filter and sort
  const [filterType, setFilterType] = useState('all'); // 'all' | 'mentor' | 'freshling' | 'volunteer'
  const [filterCancelled, setFilterCancelled] = useState('all'); // 'all' | 'no' | 'yes'
  const [filterGsg, setFilterGsg] = useState('all'); // 'all' | 'yes' | 'no'
  const [filterTest, setFilterTest] = useState('all'); // 'all' | 'test' | 'no-test'
  /** Mentor-only pipeline filter (also filters mentor rows when type is "all"). */
  const [filterMentorPipeline, setFilterMentorPipeline] = useState('all'); // 'all' | 'pending_staff' | 'approved' | 'excluded' | 'cancelled' | 'waiting_list' | 'partial_denied'
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'age' | 'type' | 'team' | 'email' | 'skydives' | 'sheetOrder'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  // Fetch active events from Firestore
  useEffect(() => {
    async function fetchEvents() {
      try {
        const querySnapshot = await getDocs(collection(firestore, 'events'));
        const eventsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          const activeId = await getActiveEventId();
          setSelectedEvent(
            resolveAdminDefaultEventId({
              eventIdParam: eventIdParam,
              activeEventId: activeId,
              events: eventsData,
            })
          );
        }
      } catch (err) {
        setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
  }, [eventIdParam]);

  // Fetch flyers for the selected event
  useEffect(() => {
    async function fetchFlyers() {
      if (!selectedEvent) return;
      try {
        const q = query(collection(firestore, 'flyers'), where('eventId', '==', selectedEvent));
        const querySnapshot = await getDocs(q);
        const flyersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFlyers(flyersData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch flyers: ' + err.message);
        setLoading(false);
      }
    }
    fetchFlyers();
  }, [selectedEvent]);

  // Fetch event details (for Google Sheet link) when event changes
  useEffect(() => {
    if (!selectedEvent) {
      setEventDetails(null);
      return;
    }
    (async () => {
      try {
        const eventSnap = await getDoc(doc(firestore, 'events', selectedEvent));
        setEventDetails(eventSnap.exists() ? eventSnap.data() : null);
      } catch (_) {
        setEventDetails(null);
      }
    })();
  }, [selectedEvent]);

  // Fetch teams for the selected event so we can indicate who is paired
  useEffect(() => {
    if (!selectedEvent) {
      setTeams([]);
      return;
    }
    (async () => {
      try {
        const teamsSnap = await getDocs(
          query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))
        );
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        // Non-fatal; Flyer Management should still work without team data
        setTeams([]);
      }
    })();
  }, [selectedEvent]);

  const handleActiveEventChange = (e) => {
    setSelectedEvent(e.target.value);
    setFuryRegistrationHelpOpen(false);
    setLoading(true);
  };

  // Next test flyer index for this event (for generating test email)
  const nextTestFlyerIndex = useMemo(() => {
    const testFlyers = flyers.filter((f) => f.test && f.eventId === selectedEvent);
    return testFlyers.length + 1;
  }, [flyers, selectedEvent]);

  // Consolidated flyers (one row per person, merging manifest tickets by email)
  const consolidatedFlyers = useMemo(() => {
    const byKey = new Map();
    flyers.forEach((flyer) => {
      const emailKey = (flyer.email || '').toString().toLowerCase().trim();
      const fallbackKey = (flyer.name || flyer.id || '').toString().toLowerCase().trim();
      const key = emailKey || fallbackKey;
      if (!key) {
        // If there is truly no stable key, keep as-is using id so we don't drop data.
        byKey.set(flyer.id, flyer);
        return;
      }

      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, flyer);
        return;
      }

      // Merge manifest tickets so sub-events consolidate across duplicates.
      const mergedManifestTickets = {
        ...(existing.manifestTickets || {}),
        ...(flyer.manifestTickets || {}),
      };
      Object.keys(flyer.manifestTickets || {}).forEach((label) => {
        const prev = existing.manifestTickets?.[label] || 0;
        const next = flyer.manifestTickets?.[label] || 0;
        mergedManifestTickets[label] = prev + next;
      });

      const merged = {
        ...existing,
        // Keep a stable id for row keys and editing; prefer the existing one.
        id: existing.id || flyer.id,
        manifestTickets: mergedManifestTickets,
        // Union of any email templates sent.
        emailTemplatesSent: Array.from(
          new Set([
            ...(Array.isArray(existing.emailTemplatesSent) ? existing.emailTemplatesSent : []),
            ...(Array.isArray(flyer.emailTemplatesSent) ? flyer.emailTemplatesSent : []),
          ])
        ),
      };

      // Same email can be a competitor (freshling) and a crew mentor; do not let the first-seen type hide the other.
      merged.type =
        flyerTypeRank(flyer.type) > flyerTypeRank(existing.type)
          ? (flyer.type || existing.type)
          : (existing.type || flyer.type);

      // Prefer non-empty / truthy values from either record for key fields used in the table.
      if (!existing.status && flyer.status) merged.status = flyer.status;
      if (!existing.recurrency && flyer.recurrency) merged.recurrency = flyer.recurrency;
      if (!existing.recurrencyUpdate && flyer.recurrencyUpdate) {
        merged.recurrencyUpdate = flyer.recurrencyUpdate;
      }
      if (!existing.sheetOrder && flyer.sheetOrder) merged.sheetOrder = flyer.sheetOrder;
      if (!existing.jumpNumber && flyer.jumpNumber) merged.jumpNumber = flyer.jumpNumber;
      if (!existing.notes && flyer.notes) merged.notes = flyer.notes;
      if (existing.cancelled !== true && flyer.cancelled === true) merged.cancelled = true;

      byKey.set(key, merged);
    });

    return Array.from(byKey.values());
  }, [flyers]);

  // Filtered and sorted flyers for display (one row per consolidated flyer)
  const displayFlyers = useMemo(() => {
    let list = consolidatedFlyers;
    if (filterType !== 'all') list = list.filter(f => f.type === filterType);
    if (filterCancelled === 'yes') list = list.filter(f => f.cancelled);
    if (filterCancelled === 'no') list = list.filter(f => !f.cancelled);
    if (filterGsg === 'yes') list = list.filter(f => f.gsg && String(f.gsg).trim().toLowerCase() !== 'no');
    if (filterGsg === 'no') list = list.filter(f => !f.gsg || String(f.gsg).trim().toLowerCase() === 'no');
    if (filterTest === 'test') list = list.filter(f => f.test);
    if (filterTest === 'no-test') list = list.filter(f => !f.test);
    if (filterMentorPipeline !== 'all') {
      list = list.filter((f) => {
        if (f.type !== 'mentor') return true;
        if (filterMentorPipeline === 'cancelled') return !!f.cancelled;
        if (filterMentorPipeline === 'waiting_list') return !!f.waiting && !f.cancelled;
        if (f.cancelled || f.waiting) return false;
        if (filterMentorPipeline === 'excluded') return f.fmMentorPairingDecision === FM_PAIRING_EXCLUDED;
        if (filterMentorPipeline === 'partial_denied') {
          return (
            f.fmMentorPairingDecision !== FM_PAIRING_EXCLUDED &&
            f.registrationMentorStatus === 'partial_denied'
          );
        }
        if (filterMentorPipeline === 'pending_staff') {
          return (
            f.fmMentorPairingDecision !== FM_PAIRING_EXCLUDED &&
            f.registrationMentorStatus !== 'partial_denied' &&
            !isRegistrationMentorApproved(f)
          );
        }
        if (filterMentorPipeline === 'approved') {
          return f.fmMentorPairingDecision !== FM_PAIRING_EXCLUDED && isRegistrationMentorApproved(f);
        }
        return true;
      });
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'name':
          va = getDisplayName(a); vb = getDisplayName(b);
          return dir * (va.localeCompare(vb, undefined, { sensitivity: 'base' }));
        case 'age': {
          const ageA = getAgeFromYear(a.yearBorn); const ageB = getAgeFromYear(b.yearBorn);
          va = ageA != null ? ageA : -1; vb = ageB != null ? ageB : -1;
          return dir * (va - vb);
        }
        case 'type':
          va = a.type || ''; vb = b.type || '';
          return dir * va.localeCompare(vb);
        case 'team':
          va = a.team || ''; vb = b.team || '';
          return dir * va.localeCompare(vb);
        case 'email':
          va = a.email || ''; vb = b.email || '';
          return dir * va.localeCompare(vb);
        case 'skydives': {
          const jumpNum = (f) => { const n = parseInt(f?.jumpNumber, 10); return Number.isFinite(n) ? n : -1; };
          va = jumpNum(a); vb = jumpNum(b);
          return dir * (va - vb);
        }
        case 'sheetOrder': {
          const parseOrder = (f) => {
            const raw = (f.sheetOrder || '').toString().trim().toLowerCase();
            if (!raw || raw === 'x') return Number.POSITIVE_INFINITY;
            const n = parseInt(raw, 10);
            return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY - 1;
          };
          va = parseOrder(a); vb = parseOrder(b);
          return dir * (va - vb);
        }
        default:
          return 0;
      }
    });
    if (searchFilterParam) {
      list = list.filter((f) => {
        const email = (f.email || '').toString().toLowerCase();
        const name = (f.name || '').toString().toLowerCase();
        const properName = (f.properName || '').toString().toLowerCase();
        return (
          email.includes(searchFilterParam) ||
          name.includes(searchFilterParam) ||
          properName.includes(searchFilterParam)
        );
      });
    }
    return list;
  }, [
    consolidatedFlyers,
    filterType,
    filterCancelled,
    filterGsg,
    filterTest,
    filterMentorPipeline,
    sortBy,
    sortDir,
    searchFilterParam,
  ]);

  // Map of flyer email → membership in any team(s) for the current event.
  const teamMembershipByEmail = useMemo(() => {
    const map = new Map();
    teams.forEach((team) => {
      const mentorEmail = (team.mentor || '').toLowerCase().trim();
      if (mentorEmail) {
        const existing = map.get(mentorEmail) || { asMentor: false, asFreshling: false, teams: [] };
        existing.asMentor = true;
        existing.teams.push(team);
        map.set(mentorEmail, existing);
      }
      (team.freshlings || []).forEach((email) => {
        const key = (email || '').toLowerCase().trim();
        if (!key) return;
        const existing = map.get(key) || { asMentor: false, asFreshling: false, teams: [] };
        existing.asFreshling = true;
        existing.teams.push(team);
        map.set(key, existing);
      });
    });
    return map;
  }, [teams]);

  // Keep details modal notes in sync when detailsFlyer changes.
  // When detailsFlyer becomes null (e.g. closing before edit), we do not call setDetailsNotesEdit here,
  // so this effect cannot loop with edit-modal state (modalOpen / editingFlyerId / newFlyer).
  useEffect(() => {
    if (detailsFlyer) {
      setDetailsNotesEdit(detailsFlyer.notes || '');
    }
  }, [detailsFlyer]);

  const saveDetailsNotes = async () => {
    if (!detailsFlyer?.id || savingDetailsNotes) return;
    const val = (detailsNotesEdit || '').trim() || null;
    setSavingDetailsNotes(true);
    try {
      await updateDoc(doc(firestore, 'flyers', detailsFlyer.id), { notes: val });
      setFlyers((prev) =>
        prev.map((f) => (f.id === detailsFlyer.id ? { ...f, notes: val } : f))
      );
      setDetailsFlyer((prev) => (prev && prev.id === detailsFlyer.id ? { ...prev, notes: val } : prev));
    } catch (err) {
      setError('Error saving notes: ' + err.message);
    } finally {
      setSavingDetailsNotes(false);
    }
  };

  const patchFlyerLocal = (flyerId, patch) => {
    setFlyers((prev) => prev.map((f) => (f.id === flyerId ? { ...f, ...patch } : f)));
    setDetailsFlyer((prev) => (prev && prev.id === flyerId ? { ...prev, ...patch } : prev));
  };

  const handleMentorExcludeFromPairing = async (flyer) => {
    if (!flyer?.id) return;
    const note = window.prompt('Optional note for admins:', '') ?? '';
    try {
      const payload = {
        fmMentorPairingDecision: FM_PAIRING_EXCLUDED,
        fmMentorPairingNote: note.trim() || null,
      };
      await updateDoc(doc(firestore, 'flyers', flyer.id), payload);
      patchFlyerLocal(flyer.id, payload);
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const handleMentorClearPairingExclusion = async (flyer) => {
    if (!flyer?.id) return;
    try {
      const payload = { fmMentorPairingDecision: null, fmMentorPairingNote: null };
      await updateDoc(doc(firestore, 'flyers', flyer.id), payload);
      patchFlyerLocal(flyer.id, payload);
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const handleMentorMarkManualApproved = async (flyer) => {
    if (!flyer?.id) return;
    try {
      const payload = {
        registrationMentorManualOverride: true,
        registrationMentorManualOverrideAt: serverTimestamp(),
        registrationMentorApproved: true,
        registrationMentorStatus: 'approved',
      };
      await updateDoc(doc(firestore, 'flyers', flyer.id), payload);
      patchFlyerLocal(flyer.id, {
        registrationMentorManualOverride: true,
        registrationMentorApproved: true,
        registrationMentorStatus: 'approved',
      });
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const handleMentorClearManualApproved = async (flyer) => {
    if (!flyer?.id) return;
    try {
      const payload = {
        registrationMentorManualOverride: false,
        registrationMentorManualOverrideAt: null,
      };
      await updateDoc(doc(firestore, 'flyers', flyer.id), payload);
      patchFlyerLocal(flyer.id, payload);
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const saveFlyerManifestTickets = useCallback(
    async (flyerId, label, ticketsNum, usedNum) => {
      if (!flyerId || label == null) return;
      const flyer = flyers.find((f) => f.id === flyerId);
      if (!flyer) return;
      const used = isDemandOnlyUsageLabel(label) ? 0 : usedNum;
      const nextManifest = { ...(flyer.manifestTickets || {}), [label]: ticketsNum };
      const nextUsage = {
        ...(flyer.usage || {}),
        [label]: { purchased: ticketsNum, used },
      };
      setSavingManifestTickets(true);
      try {
        await updateDoc(doc(firestore, 'flyers', flyerId), {
          manifestTickets: nextManifest,
          usage: nextUsage,
        });
        setFlyers((prev) =>
          prev.map((f) =>
            f.id === flyerId ? { ...f, manifestTickets: nextManifest, usage: nextUsage } : f
          )
        );
        setDetailsFlyer((prev) =>
          prev && prev.id === flyerId
            ? { ...prev, manifestTickets: nextManifest, usage: nextUsage }
            : prev
        );
      } catch (e) {
        console.error('Failed to save manifest tickets:', e);
        setError('Failed to save tickets: ' + (e.message || String(e)));
      } finally {
        setSavingManifestTickets(false);
      }
    },
    [flyers]
  );

  const [savingJumpCompletionParticipationUnit, setSavingJumpCompletionParticipationUnit] = useState(false);

  const saveFuryJumpCompletionParticipationUnitId = async (flyerId, value) => {
    if (!flyerId || savingJumpCompletionParticipationUnit) return;
    const trimmed = (value || '').trim();
    setSavingJumpCompletionParticipationUnit(true);
    try {
      const payload = trimmed ?
        { furyJumpCompletionParticipationUnitId: trimmed } :
        { furyJumpCompletionParticipationUnitId: deleteField() };
      await updateDoc(doc(firestore, 'flyers', flyerId), payload);
      setFlyers((prev) =>
        prev.map((f) => {
          if (f.id !== flyerId) return f;
          if (trimmed) return { ...f, furyJumpCompletionParticipationUnitId: trimmed };
          const { furyJumpCompletionParticipationUnitId: _omit, ...rest } = f;
          return rest;
        })
      );
      setDetailsFlyer((prev) => {
        if (!prev || prev.id !== flyerId) return prev;
        if (trimmed) return { ...prev, furyJumpCompletionParticipationUnitId: trimmed };
        const { furyJumpCompletionParticipationUnitId: _omit, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setError(err.message || 'Failed to save participation unit id');
    } finally {
      setSavingJumpCompletionParticipationUnit(false);
    }
  };

  const saveManifestCheckInRequired = async (flyerId, value) => {
    if (!flyerId || savingManifestCheckInRequired) return;
    setSavingManifestCheckInRequired(true);
    try {
      await updateDoc(doc(firestore, 'flyers', flyerId), {
        manifestCheckInRequired: value === true,
      });
      setFlyers((prev) =>
        prev.map((f) =>
          f.id === flyerId
            ? { ...f, manifestCheckInRequired: value === true }
            : f
        )
      );
      setDetailsFlyer((prev) =>
        prev && prev.id === flyerId
          ? { ...prev, manifestCheckInRequired: value === true }
          : prev
      );
    } catch (err) {
      setError('Error saving manifest check-in flag: ' + err.message);
    } finally {
      setSavingManifestCheckInRequired(false);
    }
  };

  const highlightMatcher = (flyer) => {
    if (!flyerParam) return false;
    const email = (flyer.email || '').toString().toLowerCase();
    const name = (flyer.name || '').toString().toLowerCase();
    const properName = (flyer.properName || '').toString().toLowerCase();
    return (
      email === flyerParam ||
      name === flyerParam ||
      properName === flyerParam
    );
  };

  const isWithdrawnOrDuplicate = (flyer) => {
    const statusText = (flyer.status || '').toString().toLowerCase();
    const notesText = (flyer.notes || '').toString().toLowerCase();
    return (
      statusText.includes('withdrawn') ||
      statusText.includes('duplicate') ||
      notesText.includes('withdrawn') ||
      notesText.includes('duplicate')
    );
  };

  const handleEmailSent = async ({ selectedTemplate, context: emailContext }) => {
    if (!emailContext || !emailContext.flyerId) return;
    const flyerId = emailContext.flyerId;
    const templateKey = selectedTemplate || 'custom';
    try {
      await updateDoc(doc(firestore, 'flyers', flyerId), {
        emailTemplatesSent: arrayUnion(templateKey),
      });
      setFlyers((prev) =>
        prev.map((f) =>
          f.id === flyerId
            ? {
                ...f,
                emailTemplatesSent: Array.from(
                  new Set([...(Array.isArray(f.emailTemplatesSent) ? f.emailTemplatesSent : []), templateKey])
                ),
              }
            : f
        )
      );
    } catch (err) {
      setError('Error updating email status: ' + err.message);
    }
  };

  // Handle add/edit flyer form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setFlyerFormInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setFlyerFormInvalidNames(new Set());
    try {
      let fullName = `${newFlyer.firstName} ${newFlyer.lastName}`.trim();
      if (!fullName && newFlyer.test) fullName = `Test ${newFlyer.type} ${nextTestFlyerIndex}`;
      const notesVal = (newFlyer.notes || '').trim() || null;

      if (editingFlyerId) {
        const existing = flyers.find(f => f.id === editingFlyerId);
        const { id: _id, ...rest } = existing;
        const merged = {
          ...rest,
          email: newFlyer.email,
          name: fullName,
          type: newFlyer.type,
          jumpNumber: newFlyer.jumpNumber,
          eventId: selectedEvent,
          notes: notesVal,
          test: newFlyer.test === true,
          // For freshlings, store structured name fields so display helpers work consistently
          ...(newFlyer.type === 'freshling'
            ? {
                properName: fullName || null,
                lastName: (newFlyer.lastName || '').trim() || null,
              }
            : {}),
        };
        const flyerData = Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined));
        const flyerDocRef = doc(firestore, 'flyers', editingFlyerId);
        await updateDoc(flyerDocRef, flyerData);
        setFlyers(
          flyers.map(f =>
            f.id === editingFlyerId ? { id: editingFlyerId, ...flyerData } : f
          )
        );
        setEditingFlyerId(null);
      } else {
        const flyerData = {
          email: newFlyer.email,
          name: fullName,
          type: newFlyer.type,
          jumpNumber: newFlyer.jumpNumber,
          eventId: selectedEvent,
          notes: notesVal,
          test: newFlyer.test === true,
          ...(newFlyer.type === 'freshling'
            ? {
                properName: fullName || null,
                lastName: (newFlyer.lastName || '').trim() || null,
              }
            : {}),
        };
        const docRef = await addDoc(collection(firestore, 'flyers'), {
          ...flyerData,
          createdAt: serverTimestamp(),
        });
        setFlyers([...flyers, { id: docRef.id, ...flyerData, createdAt: new Date() }]);
      }
      setNewFlyer({ email: '', firstName: '', lastName: '', type: 'mentor', jumpNumber: '', notes: '', test: false });
      setModalOpen(false);
    } catch (err) {
      setError('Error saving flyer: ' + err.message);
    }
  };

  const handleDelete = async (flyerId) => {
    try {
      await deleteDoc(doc(firestore, 'flyers', flyerId));
      setFlyers(flyers.filter(f => f.id !== flyerId));
    } catch (err) {
      setError('Error deleting flyer: ' + err.message);
    }
  };

  const handleRecurrencyChange = async (flyerId, value) => {
    const val = value === '' ? null : value;
    try {
      await updateDoc(doc(firestore, 'flyers', flyerId), { recurrency: val });
      setFlyers(flyers.map(f => f.id === flyerId ? { ...f, recurrency: val } : f));
    } catch (err) {
      setError('Error updating recurrency: ' + err.message);
    }
  };

  const getRecurrencySlotLabel = useCallback(
    (timeSlotId) => {
      const slots = eventDetails?.recurrencyTimeSlots || [];
      const slot = slots.find((s) => s.id === timeSlotId);
      if (!slot) return timeSlotId || '';
      if (slot.checkIn && slot.start) return `Check in ${slot.checkIn}, ${slot.start} class begins`;
      return slot.label || slot.start || timeSlotId;
    },
    [eventDetails?.recurrencyTimeSlots]
  );

  const flyerTableExportRows = useMemo(() => {
    return displayFlyers.map((flyer) => {
      const emailKey = (flyer.email || '').toLowerCase().trim();
      const membership = teamMembershipByEmail.get(emailKey);
      const manifestTickets = flyer.manifestTickets || {};
      const subEvents = SUB_EVENT_DEFS.filter(
        (def) => manifestTickets[def.key] && manifestTickets[def.key] > 0
      );
      const extraSubEvents = Object.keys(manifestTickets)
        .filter(
          (label) =>
            !SUB_EVENT_DEFS.some((def) => def.key === label) && manifestTickets[label] > 0
        )
        .sort();
      const subEventsStr = [
        ...subEvents.map((d) => d.shortLabel),
        ...extraSubEvents,
      ].join(', ');
      const sentTemplateKeys = new Set(
        Array.isArray(flyer.emailTemplatesSent) ? flyer.emailTemplatesSent : []
      );
      const sentTemplateLabels = Array.from(sentTemplateKeys)
        .map((key) => EMAIL_TEMPLATE_LABELS[key] || key)
        .sort();
      const ru = flyer.recurrencyUpdate;
      let recUpdate = '—';
      if (ru) {
        const slotPart = getRecurrencySlotLabel(ru.timeSlotId) || '—';
        const datePart =
          ru.submittedAt && ru.submittedAt.toDate
            ? ru.submittedAt.toDate().toLocaleDateString()
            : '';
        recUpdate = datePart ? `${slotPart} (${datePart})` : slotPart;
      }
      const teamStr =
        membership && membership.teams.length > 0
          ? membership.teams
              .map((t) => (t.teamNumber ? `Team ${t.teamNumber}` : 'Paired'))
              .join(', ')
          : '—';
      const displayName =
        flyer.type === 'freshling' ? getFreshlingDisplayName(flyer) : flyer.name;
      const typeLabel =
        flyer.type === 'freshling' ? 'Freshling' : flyer.type === 'volunteer' ? 'Volunteer' : 'Mentor';
      return {
        name: displayName || '—',
        type: typeLabel,
        email: (flyer.email || '').trim(),
        test: flyer.test ? 'Test' : '—',
        manifestCheckIn: flyer.manifestCheckInRequired === true ? 'Yes' : 'No',
        subEvents: subEventsStr || '—',
        skydives: flyer.type === 'freshling' ? flyer.jumpNumber || '—' : '—',
        sheetOrder: flyer.type === 'freshling' ? flyer.sheetOrder || '—' : '—',
        rec:
          flyer.type === 'freshling'
            ? flyer.recurrency ?? '—'
            : flyer.recurrency || '—',
        recUpdate,
        status: flyer.status || '—',
        emailsSent: sentTemplateLabels.length > 0 ? sentTemplateLabels.join(', ') : '—',
        team: teamStr,
      };
    });
  }, [displayFlyers, teamMembershipByEmail, getRecurrencySlotLabel]);

  useEffect(() => {
    if (!flyerTableExport) return;
    setFlyerExportCols(
      new Set(FLYER_TABLE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [flyerTableExport]);

  const openFlyerTableExport = useCallback((mode) => {
    if (!flyerTableExportRows.length) {
      window.alert('No flyers match the current filters.');
      return;
    }
    setFlyerTableExport({ mode });
  }, [flyerTableExportRows]);

  const resetFlyerExportCols = () => {
    setFlyerExportCols(
      new Set(FLYER_TABLE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runFlyerTableExport = (selectedColumnIds) => {
    if (!flyerTableExport) return;
    const { mode } = flyerTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : flyerExportCols;
    const active = FLYER_TABLE_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = flyerTableExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const ev =
      activeEvents.find((e) => e.id === selectedEvent) ||
      eventDetails ||
      null;
    const evTitle = ev?.title || selectedEvent || 'Flyers';
    const sub = 'Flyer list';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setFlyerTableExport(null);
  };

  const copyFlyerNames = useCallback(() => {
    const names = displayFlyers
      .map((flyer) =>
        flyer.type === 'freshling' ? getFreshlingDisplayName(flyer) : flyer.name || ''
      )
      .map((name) => (name || '').toString().trim())
      .filter(Boolean);
    if (!names.length) return;
    copyText(names.join('\n'));
  }, [displayFlyers]);

  const copyFlyerEmails = useCallback(() => {
    const emails = displayFlyers
      .map((flyer) => (flyer.email || '').toString().trim())
      .filter(Boolean);
    if (!emails.length) return;
    copyText(emails.join('\n'));
  }, [displayFlyers]);

  const flyerCopyMenuItems = useMemo(
    () => [
      {
        label: 'Copy table…',
        onSelect: () => openFlyerTableExport('copy'),
        showCopiedFeedback: false,
      },
      {
        label: 'Copy names',
        onSelect: copyFlyerNames,
      },
      {
        label: 'Copy emails',
        onSelect: copyFlyerEmails,
      },
    ],
    [copyFlyerEmails, copyFlyerNames, openFlyerTableExport]
  );

  const handleEdit = useCallback((flyer, options = {}) => {
    const dbg = (...args) => console.log('[FlyerManagement handleEdit]', ...args);
    if (!flyer) return;
    const { closeDetailsFirst = false } = options;
    dbg('start', { closeDetailsFirst, flyerId: flyer.id });
    const resolvedFlyer =
      flyers.find((f) => f.id === flyer.id) ||
      flyers.find(
        (f) =>
          (f.email || '').toLowerCase().trim() ===
          (flyer.email || '').toLowerCase().trim()
      ) ||
      flyer;
    const firstName = getFirstName(resolvedFlyer);
    let lastName = '';
    if (resolvedFlyer.type === 'freshling') {
      const explicitLast = (resolvedFlyer.lastName ?? '').toString().trim();
      if (explicitLast) {
        lastName = explicitLast;
      } else {
        const formal = (resolvedFlyer.properName || resolvedFlyer.name || '')
          .toString()
          .trim();
        lastName = formal.split(/\s+/).slice(1).join(' ').trim();
      }
    } else {
      const full = (resolvedFlyer.name || '').toString().trim();
      lastName = full.split(/\s+/).slice(1).join(' ').trim();
    }
    const openEditModal = () => {
      dbg('openEditModal: before setEditingFlyerId', resolvedFlyer.id || flyer.id);
      setEditingFlyerId(resolvedFlyer.id || flyer.id || null);
      dbg('openEditModal: after setEditingFlyerId (scheduled)');
      dbg('openEditModal: before setNewFlyer');
      setNewFlyer({
        email: resolvedFlyer.email || '',
        firstName,
        lastName,
        type: resolvedFlyer.type || 'mentor',
        jumpNumber: resolvedFlyer.jumpNumber || '',
        notes: resolvedFlyer.notes || '',
        test: resolvedFlyer.test === true,
      });
      dbg('openEditModal: after setNewFlyer (scheduled)');
      dbg('openEditModal: before setModalOpen(true)');
      setModalOpen(true);
      dbg('openEditModal: after setModalOpen(true) (scheduled)');
    };
    if (closeDetailsFirst) {
      dbg('closeDetailsFirst: before setDetailsFlyer(null)');
      setDetailsFlyer(null);
      dbg('closeDetailsFirst: after setDetailsFlyer(null) (scheduled)');
      // Let the details modal unmount before opening the edit modal.
      dbg('closeDetailsFirst: scheduling setTimeout(0, openEditModal)');
      window.setTimeout(() => {
        dbg('setTimeout callback: invoking openEditModal');
        openEditModal();
        dbg('setTimeout callback: openEditModal finished');
      }, 0);
      dbg('closeDetailsFirst: handleEdit sync path done (timeout pending)');
      return;
    }
    dbg('direct path: invoking openEditModal');
    openEditModal();
    dbg('direct path: handleEdit sync path done');
  }, [flyers]);

  const openAddTestFlyer = (type) => {
    const n = nextTestFlyerIndex;
    const email = `test-${type}-${selectedEvent}-${n}@test.local`;
    setEditingFlyerId(null);
    setNewFlyer({
      email,
      firstName: '',
      lastName: '',
      type,
      jumpNumber: type === 'freshling' ? '0' : '',
      notes: '',
      test: true,
    });
    setModalOpen(true);
  };

  // Delete All: deletes all flyers for the selected event after confirmation. Returns true if flyers were cleared.
  const handleDeleteAll = async () => {
    if (!selectedEvent) {
      setError('Please select an event first.');
      return false;
    }
    if (!window.confirm("Are you sure you want to delete all flyers for this event?")) return false;
    setError('');
    try {
      const q = query(collection(firestore, 'flyers'), where('eventId', '==', selectedEvent));
      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      if (docs.length === 0) {
        setFlyers([]);
        return true;
      }
      // Firestore batch allows max 500 operations; chunk if needed
      const BATCH_SIZE = 500;
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        const chunk = docs.slice(i, i + BATCH_SIZE);
        chunk.forEach((docSnap) => batch.delete(docSnap.ref));
        await batch.commit();
      }
      setFlyers([]);
      return true;
    } catch (err) {
      setError('Error deleting all flyers: ' + err.message);
      return false;
    }
  };

  const openAddFlyerFromAdvanced = () => {
    setAdvancedSettingsOpen(false);
    setEditingFlyerId(null);
    setNewFlyer({ email: '', firstName: '', lastName: '', type: 'mentor', jumpNumber: '', notes: '', test: false });
    setModalOpen(true);
  };

  const openAddTestFlyerFromAdvanced = (type) => {
    setAdvancedSettingsOpen(false);
    openAddTestFlyer(type);
  };

  return (
    <div className="flyer-management">
      <h2>Flyer Management</h2>
      {error && <p className="error">{error}</p>}
      <div className="dropdown-container">
        <label htmlFor="activeEvent">Select Event:</label>
        <select id="activeEvent" value={selectedEvent} onChange={handleActiveEventChange}>
          {activeEvents.map(event => (
            <option key={event.id} value={event.id}>{event.title}</option>
          ))}
        </select>
      </div>
      <div className="flyer-management-tabs">
        <button
          className={`flyer-management-tab${activeTab === 'flyers' ? ' flyer-management-tab--active' : ''}`}
          onClick={() => setActiveTab('flyers')}
        >
          Flyers
        </button>
        <button
          className={`flyer-management-tab${activeTab === 'homeDz' ? ' flyer-management-tab--active' : ''}`}
          onClick={() => setActiveTab('homeDz')}
        >
          Home DZ
        </button>
      </div>
      {activeTab === 'homeDz' && (
        <HomeDzTab flyers={flyers} selectedEvent={selectedEvent} />
      )}
      {activeTab === 'flyers' && (<>
      <div className="google-sheet-import-section">
        {eventDetails?.registrationImportSource === 'fury_api' ? (
          <div className="flyer-mgmt-fury-help-wrap">
            <p className="sheet-link-status sheet-link-status--fury-help">
              <span>Fury Registration</span>
              <button
                type="button"
                className="flyer-mgmt-help-q"
                title={FURY_REGISTRATION_HELP_TEXT}
                aria-label="About Fury Registration sync"
                aria-expanded={furyRegistrationHelpOpen}
                aria-controls="flyer-mgmt-fury-help-panel"
                onClick={() => setFuryRegistrationHelpOpen((open) => !open)}
              >
                ?
              </button>
            </p>
            {furyRegistrationHelpOpen && (
              <div
                id="flyer-mgmt-fury-help-panel"
                className="flyer-mgmt-fury-help-panel"
                role="region"
                aria-label="Fury Registration help"
              >
                <p>{FURY_REGISTRATION_HELP_TEXT}</p>
                <button
                  type="button"
                  className="flyer-mgmt-fury-help-close"
                  onClick={() => setFuryRegistrationHelpOpen(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        ) : eventDetails?.freshlingsSpreadsheetId ? (
          <p className="sheet-link-status">
            <a href={`https://docs.google.com/spreadsheets/d/${eventDetails.freshlingsSpreadsheetId}`} target="_blank" rel="noopener noreferrer">Open sheet</a>
            {' · Freshlings: '}{(eventDetails.freshlingsSheetName || 'Freshlings')}
            {' · Mentors: '}{(eventDetails.mentorsSheetName || 'Mentors')}
            {' — Import freshlings/mentors from Event Management.'}
          </p>
        ) : (
          <p className="sheet-link-status not-linked">No sheet linked. Edit this event in Event Management and add a Freshlings Google Sheet URL or ID.</p>
        )}
      </div>
      <div className="flyer-advanced-settings-row">
        <button type="button" className="flyer-advanced-settings-trigger" onClick={() => setAdvancedSettingsOpen(true)}>
          Advanced settings
        </button>
      </div>
      <div className="table-controls">
        <div className="table-filters">
          <label>
            Type: <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All</option>
              <option value="freshling">Freshling</option>
              <option value="mentor">Mentor</option>
              <option value="volunteer">Volunteer</option>
            </select>
          </label>
          <label>
            Cancelled: <select value={filterCancelled} onChange={(e) => setFilterCancelled(e.target.value)}>
              <option value="all">All</option>
              <option value="no">No</option>
              <option value="yes">Yes only</option>
            </select>
          </label>
          <label>
            GSG: <select value={filterGsg} onChange={(e) => setFilterGsg(e.target.value)}>
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label>
            Test: <select value={filterTest} onChange={(e) => setFilterTest(e.target.value)}>
              <option value="all">All</option>
              <option value="no-test">Exclude test</option>
              <option value="test">Test only</option>
            </select>
          </label>
          <label>
            Mentor pipeline:{' '}
            <select
              value={filterMentorPipeline}
              onChange={(e) => setFilterMentorPipeline(e.target.value)}
              title="Filters mentor rows; other flyer types pass through unchanged."
            >
              <option value="all">All</option>
              <option value="pending_staff">Pending staff</option>
              <option value="approved">Staff-approved</option>
              <option value="excluded">FM excluded</option>
              <option value="waiting_list">Waiting list</option>
              <option value="partial_denied">Partial denial</option>
              <option value="cancelled">Off roster</option>
            </select>
          </label>
          <label>
            Sort: <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Name</option>
              <option value="age">Age</option>
              <option value="type">Type</option>
              <option value="team">Team</option>
              <option value="email">Email</option>
              <option value="skydives">Skydives</option>
              <option value="sheetOrder">Sheet order</option>
            </select>
            <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </label>
        </div>
        <div className="table-count-row">
          <p className="table-count">{displayFlyers.length} of {flyers.length} flyers</p>
          <button
            type="button"
            className="flyer-export-print-btn"
            onClick={() => openFlyerTableExport('print')}
            disabled={loading || !flyerTableExportRows.length}
          >
            Print table
          </button>
          <AdminCopyMenu
            ariaLabel="Copy flyers table"
            disabled={loading || !flyerTableExportRows.length}
            items={flyerCopyMenuItems}
          />
        </div>
      </div>
      <div className="table-container">
        {loading ? (
          <p>Loading flyers...</p>
        ) : flyers.length ? (
          <table className="flyer-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Test</th>
                <th>Manifest check-in</th>
                <th>Sub-events</th>
                <th>Skydives</th>
                <th>Sheet #</th>
                <th>Rec</th>
                <th>Rec update</th>
                <th>Status</th>
                <th>Emails</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayFlyers.map(flyer => {
                const baseClass = flyer.cancelled
                  ? 'cancelled'
                  : isWithdrawnOrDuplicate(flyer)
                    ? 'flyer-row-dimmed'
                    : '';
                const highlightClass = highlightMatcher(flyer) ? 'flyer-row-highlight' : '';
                const testClass = flyer.test ? 'flyer-row--test' : '';
                const rowClass = [baseClass, highlightClass, testClass].filter(Boolean).join(' ');
                const emailKey = (flyer.email || '').toLowerCase().trim();
                const membership = teamMembershipByEmail.get(emailKey);
                const manifestTickets = flyer.manifestTickets || {};
                const subEvents = SUB_EVENT_DEFS.filter(
                  (def) => manifestTickets[def.key] && manifestTickets[def.key] > 0
                );
                const extraSubEvents = Object.keys(manifestTickets)
                  .filter(
                    (label) =>
                      !SUB_EVENT_DEFS.some((def) => def.key === label) &&
                      manifestTickets[label] > 0
                  )
                  .sort();
                const sentTemplateKeys = new Set(
                  Array.isArray(flyer.emailTemplatesSent) ? flyer.emailTemplatesSent : []
                );
                const sentTemplateLabels = Array.from(sentTemplateKeys)
                  .map((key) => EMAIL_TEMPLATE_LABELS[key] || key)
                  .sort();
                return (
                <tr
                  key={flyer.id}
                  className={rowClass}
                >
                  <td>
                    <div className="flyer-name-cell">
                      <button
                        type="button"
                        className="flyer-name-button flyer-name-link"
                        title={flyer.type === 'freshling' ? getFreshlingDisplayName(flyer) : flyer.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailsFlyer(flyer);
                        }}
                      >
                        <span className="flyer-name-text">
                          {flyer.type === 'freshling' ? getFreshlingDisplayName(flyer) : flyer.name}
                          {flyer.test && (
                            <span className="test-badge" title="Test (dummy) data">
                              TEST
                            </span>
                          )}
                        </span>
                      </button>
                      <span
                        className={[
                          'role-badge',
                          flyer.type === 'freshling'
                            ? 'role-badge--freshling'
                            : flyer.type === 'volunteer'
                              ? 'role-badge--volunteer'
                              : 'role-badge--mentor',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={
                          membership && membership.teams.length > 0
                            ? membership.teams
                                .map((t) =>
                                  t.teamNumber ? `Team ${t.teamNumber}` : 'Paired team'
                                )
                                .join(', ')
                            : flyer.type === 'freshling'
                            ? 'Freshling'
                            : flyer.type === 'volunteer'
                              ? 'Volunteer'
                              : 'Mentor'
                        }
                      >
                        {flyer.type === 'freshling'
                          ? 'Freshling'
                          : flyer.type === 'volunteer'
                            ? 'Volunteer'
                            : 'Mentor'}
                      </span>
                    </div>
                    {flyer.email && (
                      <button
                        type="button"
                        className="flyer-icon-btn"
                        title="Email flyer"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEmailComposer({
                            template: 'custom',
                            to: flyer.email || '',
                            bcc: '',
                            context: {
                              flyer,
                              event: eventDetails,
                              flyerId: flyer.id,
                            },
                          });
                        }}
                      >
                        ✉
                      </button>
                    )}
                  </td>
                  <td>{flyer.test ? 'Test' : '—'}</td>
                  <td
                    className={[
                      'flyer-manifest-checkin-cell',
                      flyer.manifestCheckInRequired === true && 'flyer-manifest-checkin-cell--checked',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <label className="flyer-manifest-checkin-label">
                      <input
                        type="checkbox"
                        checked={flyer.manifestCheckInRequired === true}
                        onChange={(e) =>
                          saveManifestCheckInRequired(flyer.id, e.target.checked)
                        }
                        disabled={savingManifestCheckInRequired}
                        title="Flag for morning manifest check-in"
                        aria-label="Flag for morning manifest check-in"
                      />
                    </label>
                  </td>
                  <td>
                    <div className="flyer-subevents">
                      {subEvents.map((def) => (
                        <span key={def.key} className="flyer-subevent-pill">
                          {def.shortLabel}
                        </span>
                      ))}
                      {extraSubEvents.map((label) => (
                        <span key={label} className="flyer-subevent-pill flyer-subevent-pill-muted">
                          {label}
                        </span>
                      ))}
                      {subEvents.length === 0 && extraSubEvents.length === 0 && <span>-</span>}
                    </div>
                  </td>
                  <td>{flyer.type === 'freshling' ? (flyer.jumpNumber || '-') : '-'}</td>
                  <td>{flyer.type === 'freshling' ? (flyer.sheetOrder || '-') : '-'}</td>
                  <td>
                    {flyer.type === 'freshling' ? (
                      <select
                        value={flyer.recurrency ?? ''}
                        onChange={(e) => handleRecurrencyChange(flyer.id, e.target.value || null)}
                        className="flyer-recurrency-select"
                      >
                        <option value="">—</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Unsure">Unsure</option>
                      </select>
                    ) : (
                      flyer.recurrency || '-'
                    )}
                  </td>
                  <td className="flyer-rec-update-cell">
                    {flyer.recurrencyUpdate ? (
                      <span
                        title={[
                          flyer.recurrencyUpdate.lastJumpDate && `Last jump: ${flyer.recurrencyUpdate.lastJumpDate}`,
                          flyer.recurrencyUpdate.availableWednesdayMorning && `Wed AM: ${flyer.recurrencyUpdate.availableWednesdayMorning}`,
                          flyer.recurrencyUpdate.notes && `Notes: ${flyer.recurrencyUpdate.notes}`,
                          flyer.recurrencyUpdate.submittedAt && `Submitted: ${flyer.recurrencyUpdate.submittedAt.toDate ? flyer.recurrencyUpdate.submittedAt.toDate().toLocaleString() : String(flyer.recurrencyUpdate.submittedAt)}`,
                        ].filter(Boolean).join('\n')}
                      >
                        {getRecurrencySlotLabel(flyer.recurrencyUpdate.timeSlotId) || '—'}
                        {flyer.recurrencyUpdate.submittedAt && (
                          <span className="flyer-rec-update-date">
                            {' '}
                            {flyer.recurrencyUpdate.submittedAt.toDate
                              ? flyer.recurrencyUpdate.submittedAt.toDate().toLocaleDateString()
                              : ''}
                          </span>
                        )}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{flyer.status || '-'}</td>
                  <td>
                    {sentTemplateLabels.length > 0
                      ? sentTemplateLabels.join(', ')
                      : '—'}
                  </td>
                  <td className="flyer-row-actions">
                    <button
                      type="button"
                      className="flyer-row-action flyer-row-action--edit"
                      onClick={() => handleEdit(flyer)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="flyer-row-action flyer-row-action--delete"
                      onClick={() => handleDelete(flyer.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        ) : (
          <p>No flyers found for this event.</p>
        )}
      </div>
      </>)}
      {advancedSettingsOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setAdvancedSettingsOpen(false)}>
          <div
            className="fm-modal fm-modal--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="flyer-advanced-settings-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="flyer-advanced-settings-title">
                Advanced settings
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setAdvancedSettingsOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body flyer-advanced-settings-body">
              <p className="flyer-advanced-settings-hint">Add flyers manually, create test records, or remove every flyer for this event.</p>
              <div className="flyer-advanced-settings-actions">
                <button type="button" className="add-flyer-button btn-fm-primary" onClick={openAddFlyerFromAdvanced}>
                  Add Flyer
                </button>
                <button
                  type="button"
                  className="add-test-button"
                  onClick={() => openAddTestFlyerFromAdvanced('freshling')}
                  disabled={!selectedEvent}
                >
                  Add test freshling
                </button>
                <button
                  type="button"
                  className="add-test-button"
                  onClick={() => openAddTestFlyerFromAdvanced('mentor')}
                  disabled={!selectedEvent}
                >
                  Add test mentor
                </button>
                <button
                  type="button"
                  className="add-test-button"
                  onClick={() => openAddTestFlyerFromAdvanced('volunteer')}
                  disabled={!selectedEvent}
                >
                  Add test volunteer
                </button>
                <button
                  type="button"
                  className="delete-all-button"
                  onClick={async () => {
                    const ok = await handleDeleteAll();
                    if (ok) setAdvancedSettingsOpen(false);
                  }}
                >
                  Delete All
                </button>
              </div>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setAdvancedSettingsOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for add/edit flyer */}
      {modalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setModalOpen(false)}>
          <div
            className="fm-modal fm-modal--md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="flyer-mgmt-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="flyer-mgmt-modal-title">
                {editingFlyerId
                  ? 'Edit flyer'
                  : newFlyer.test
                    ? `Add test ${newFlyer.type}`
                    : 'Add flyer'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <form noValidate onSubmit={handleSubmit} className="flyer-form">
              <div className="fm-modal-body">
              <div className="form-group">
                <label>Email:{newFlyer.test && <span className="form-label-hint"> (test-generated)</span>}</label>
                {newFlyer.test ? (
                  <input type="text" value={newFlyer.email} readOnly className="read-only-input" />
                ) : (
                  <input
                    name="flyerEmail"
                    type="email"
                    value={newFlyer.email}
                    onChange={(e) => setNewFlyer({ ...newFlyer, email: e.target.value })}
                    required
                  />
                )}
                <ModalFieldRequiredError invalidNames={flyerFormInvalidNames} fieldName="flyerEmail" />
              </div>
              <div className="form-group">
                <label>First Name:</label>
                <input
                  name="flyerFirstName"
                  type="text"
                  value={newFlyer.firstName}
                  onChange={(e) => setNewFlyer({ ...newFlyer, firstName: e.target.value })}
                  required={!newFlyer.test}
                  placeholder={newFlyer.test ? 'e.g. Test Freshling 1' : ''}
                />
                <ModalFieldRequiredError invalidNames={flyerFormInvalidNames} fieldName="flyerFirstName" />
              </div>
              <div className="form-group">
                <label>Last Name:</label>
                <input
                  name="flyerLastName"
                  type="text"
                  value={newFlyer.lastName}
                  onChange={(e) => setNewFlyer({ ...newFlyer, lastName: e.target.value })}
                  required={!newFlyer.test}
                  placeholder={newFlyer.test ? 'Optional' : ''}
                />
                <ModalFieldRequiredError invalidNames={flyerFormInvalidNames} fieldName="flyerLastName" />
              </div>
              <div className="form-group">
                <label>Type:</label>
                <select
                  name="flyerType"
                  value={newFlyer.type}
                  onChange={(e) => setNewFlyer({ ...newFlyer, type: e.target.value })}
                  required
                >
                  {FLYER_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <ModalFieldRequiredError invalidNames={flyerFormInvalidNames} fieldName="flyerType" />
              </div>
              {newFlyer.type === 'freshling' && (
                <div className="form-group">
                  <label>Jump Number (Skydives):</label>
                  <input
                    type="text"
                    value={newFlyer.jumpNumber}
                    onChange={(e) => setNewFlyer({ ...newFlyer, jumpNumber: e.target.value })}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Notes (team changes, thoughts; kept on re-import):</label>
                <textarea
                  rows={3}
                  value={newFlyer.notes}
                  onChange={(e) => setNewFlyer({ ...newFlyer, notes: e.target.value })}
                  placeholder="Optional notes about this person"
                />
              </div>
              </div>
              <div className="fm-modal-footer">
                <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  {editingFlyerId ? 'Update flyer' : 'Add flyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Details modal for per-flyer secondary information */}
      <FlyerDetailModal
        flyer={detailsFlyer}
        onClose={() => setDetailsFlyer(null)}
        onEdit={(flyer) => handleEdit(flyer, { closeDetailsFirst: true })}
        teams={teams}
        flyers={flyers}
        eventId={selectedEvent}
        notes={detailsNotesEdit}
        onNotesChange={setDetailsNotesEdit}
        onSaveNotes={saveDetailsNotes}
        savingNotes={savingDetailsNotes}
        onSaveManifestTickets={saveFlyerManifestTickets}
        savingManifestTickets={savingManifestTickets}
        onSaveManifestCheckInRequired={saveManifestCheckInRequired}
        savingManifestCheckInRequired={savingManifestCheckInRequired}
        onSaveFuryJumpCompletionParticipationUnitId={saveFuryJumpCompletionParticipationUnitId}
        savingFuryJumpCompletionParticipationUnitId={savingJumpCompletionParticipationUnit}
        recurrencyTimeSlots={eventDetails?.recurrencyTimeSlots || []}
        onMentorExcludeFromPairing={handleMentorExcludeFromPairing}
        onMentorClearPairingExclusion={handleMentorClearPairingExclusion}
        onMentorMarkManualApproved={handleMentorMarkManualApproved}
        onMentorClearManualApproved={handleMentorClearManualApproved}
      />
      {flyerTableExport && (
        <AdminColumnExportModal
          mode={flyerTableExport.mode}
          columns={FLYER_TABLE_EXPORT_COLS}
          selectedIds={flyerExportCols}
          onChangeSelected={setFlyerExportCols}
          onCancel={() => setFlyerTableExport(null)}
          onConfirm={runFlyerTableExport}
          onResetDefaults={resetFlyerExportCols}
        />
      )}
      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
};

export default FlyerManagement;
